import React, { Suspense, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Squirrel } from "../../components/spinner/Squirrel";
import { loadableCompleteVerifiedRecordsAtom, isHydrated } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { NotFound } from "../../components/NotFound";
import { useTranslation } from "react-i18next";
import { SHLinkAccessLogDto } from "@docket/consumer-app-client-typescript-axios";
import { getAPIClient } from "../../apiClient";
import { getDateTime } from "../../utils/data";

export default function LinkLogs() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);
  const [accessLogs, setAccessLogs] = useState([] as SHLinkAccessLogDto[]);
  const [allVerifiedRecords] = useAtom(loadableCompleteVerifiedRecordsAtom);

  const currRecord = React.useMemo(() => {
    if (allVerifiedRecords.state !== "hasData" || !isHydrated(allVerifiedRecords.data)) {
      return null;
    }
    return allVerifiedRecords.data.find((record) => record.record.uid === id);
  }, [allVerifiedRecords, id]);

  useEffect(() => {
    if (currRecord?.record.shLink) {
      async function fetchAccessLogs() {
        const logs = await getAPIClient().getSmartHealthLinkAccessLogs(
          currRecord!.record.shLink!.id.toString()
        );
        setAccessLogs(logs);
        setInitialized(true);
      }

      fetchAccessLogs();
    }
  }, [currRecord]);

  if (allVerifiedRecords.state !== "loading" && !currRecord?.record.shLink) {
    return <NotFound />;
  }

  if (!initialized) {
    return <Squirrel />;
  }

  return (
    <div className="flex flex-col p-6 docket-gap-1">
      <p className="mobile-text-color md:text-black text-lg font-bold">
        {t("immunizations.shlink_access_log_title")}
      </p>
      {accessLogs.length === 0 ? (
        <p className="mobile-text-color text-md">
          You have not shared this SMART® Health Link with anyone, or no one has accessed it yet.
        </p>
      ) : (
        <div className="flex flex-col docket-gap-1">
          {accessLogs.map((log, i) => (
            <div
              key={i}
              data-testid={`access-log-${i}`}
              className="py-4 px-2 bg-access-blue rounded-md flex flex-col gap-0.5 md:shadow-[0_4px_0px_0px_#0a0a0a1a] max-md:shadow-[0_4px_0px_0px_#237bb8]"
            >
              <div className="flex docket-gap-1">
                <p className="text-md text-white">Date Accessed: </p>
                <p className="text-sm text-white pt-0.5">
                  {getDateTime(log.accessedAt).toLocaleString()}
                </p>
              </div>
              <div className="flex docket-gap-1">
                <p className="text-md text-white">Time Accessed: </p>
                <p className="text-sm text-white pt-0.5">
                  {getDateTime(log.accessedAt).toFormat("hh:mm:ss")}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
